import { Alert, Box, ChakraProps, Flex } from "@biblioteksentralen/react";
import { localizedFormat } from "../../utils/date";
import { useTranslation } from "../../utils/hooks/useTranslation";
import { TimeSpans } from "./TimeSpan";
import { OpeningHoursForDate } from "./openingHoursHelper/openingHoursHelper";
import { useSelfServiceLabel } from "./utils";
import { capitalizeFirstLetter } from "@libry-content/common";
import { getSelfServiceLabel } from "../../utils/getSelfServiceLabel";
import { useCommonData } from "../layout/CommonDataProvider";

type Props = { openingHoursForDate: OpeningHoursForDate; date: Date } & ChakraProps;

export const DivergentOpeningHoursAlert = ({ openingHoursForDate, date, ...chakraProps }: Props) => {
  const { t, ts, lang } = useTranslation();
  const selfServiceLabel = useSelfServiceLabel();

  if (!openingHoursForDate.divergentHoursForDate) return null;

  const { normalHours, selfService } = openingHoursForDate;

  const divergentNormalHours = normalHours?.isSpecial;
  const divergentSelfServiceHours = selfService?.isSpecial;

  const label =
    (openingHoursForDate.normalHours?.isSpecial && ts(openingHoursForDate.normalHours.note)) ||
    (openingHoursForDate.selfService?.isSpecial && ts(openingHoursForDate.selfService.note)) ||
    t("Avvikende åpningstid");

  return (
    <Alert status="warning" border="none !important" fontSize="sm" {...chakraProps}>
      <Box fontWeight={600}>{label}</Box>
      <Flex gap="0 .3em" alignItems="center" flexWrap="wrap">
        {t("{weekday} har vi vanligvis", {
          weekday: capitalizeFirstLetter(localizedFormat(date.toISOString(), "EEEE", lang)),
        })}{" "}
        {divergentNormalHours && (
          <VanligÅpningstid normalHoursOrSelfService="normalHours" label={t("åpent")} hours={normalHours} />
        )}
        {divergentNormalHours && divergentSelfServiceHours && <Box> {t("og")} </Box>}
        {divergentSelfServiceHours && (
          <VanligÅpningstid normalHoursOrSelfService="selfService" label={selfServiceLabel} hours={selfService} />
        )}
      </Flex>
    </Alert>
  );
};

const VanligÅpningstid = (props: {
  normalHoursOrSelfService: "normalHours" | "selfService";
  hours: OpeningHoursForDate["normalHours"] | OpeningHoursForDate["selfService"];
  label: string;
}) => {
  const { t, lang } = useTranslation();
  const { site } = useCommonData();

  const spans = props.hours?.isSpecial ? props.hours.usualHoursForWeekday?.spans : props.hours?.spans;

  if (!spans?.length)
    return props.normalHoursOrSelfService === "normalHours"
      ? t("stengt")
      : `${t("ikke")} ${getSelfServiceLabel(lang, site)}`;

  return (
    <>
      {props.label} <TimeSpans spans={spans} />
    </>
  );
};
